import styled, { css } from 'styled-components';
import { Card } from 'antd';
import { Link } from 'react-router-dom';
import { QUALITY_MARK_ASPECT_RATIO, breakpoints } from 'config/config';

export const StyledProductCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.white};
  border-radius: 5px;
  font-family: ${({ theme }) => theme.fontText};

  &.hoverable {
    transition: box-shadow 0.2s ease-in-out;

    &:hover {
      box-shadow: 0px 4px 12px ${({ theme }) => theme.boxShadowColor};
    }
  }

  .product-card-cover {
    position: relative;
    aspect-ratio: 276 / 242;
    width: 100%;
    border-radius: 5px 5px 0 0;
    overflow: hidden;
    cursor: pointer;

    .product-card-image,
    .product-card-image .ant-image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }

    .product-card-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.85) 0%,
        rgba(255, 255, 255, 0) 55%
      );
      pointer-events: none;
      z-index: 1;
    }
  }

  .product-card-tags {
    position: absolute;
    top: 12px;
    left: 12px;
    right: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    z-index: 2;
  }

  .product-card-tag {
    display: flex;
    align-items: center;
    height: 22px;
    padding: 3px 4px;
    border-radius: 2px;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0.024px;

    &.top {
      background-color: ${({ theme }) => theme.topPriceBgColor};
      color: ${({ theme }) => theme.white};
    }

    &.discount {
      background-color: ${({ theme }) => theme.discountTagBg};
      color: ${({ theme }) => theme.cardTagText};
    }
  }

  .product-card-qmarks {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    height: 30px;

    .product-card-qmark {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 30px;
      padding: 3px 7px;
      border-radius: 2px;
      background-color: ${({ theme }) => theme.white};
      filter: drop-shadow(0 0 10.5px rgba(0, 0, 0, 0.2));
    }

    .product-card-qmark .ant-image {
      display: flex;
    }

    .product-card-qmark img {
      height: 24px;
      width: auto;
      aspect-ratio: ${QUALITY_MARK_ASPECT_RATIO};
      object-fit: contain;
    }
  }

  .product-card-name {
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: 12px;
    z-index: 2;
    color: ${({ theme }) => theme.white};
    font-size: 16px;
    font-weight: 600;
    line-height: 23px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    span {
      font-size: inherit;
    }
  }

  &.farm-view .product-card-cover {
    aspect-ratio: 261 / 173;
  }

  .product-card-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    flex: 1;

    span {
      font-size: inherit;
    }
  }

  .product-card-price {
    display: flex;
    align-items: baseline;
    gap: 10px;

    &:has(.price-old + .price-per-unit) {
      align-items: center;
    }

    .price-current {
      font-size: 27px;
      font-weight: 600;
      line-height: 1;
      color: ${({ theme }) => theme.navDark};
      white-space: nowrap;

      &.discounted {
        color: ${({ theme }) => theme.red};
      }

      .price-currency {
        font-size: 15px;
      }

      .price-unit {
        margin-left: 4px;
        font-size: 12px;
        font-weight: 500;
        color: ${({ theme }) => theme.navDark};
      }
    }

    .price-details {
      display: flex;
      flex-direction: column;
      gap: 2px;
      font-size: 10px;
      font-weight: 500;
      line-height: 14px;
      white-space: nowrap;

      .price-old {
        color: ${({ theme }) => theme.navDark};
        text-decoration: line-through;
      }

      .price-per-unit {
        color: ${({ theme }) => theme.cardMutedText};
      }
    }
  }

  .product-card-farm {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    line-height: 14px;
    color: ${({ theme }) => theme.cardMutedText};
    cursor: pointer;

    .product-card-farm-pin {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 14px;
      height: 14px;

      img {
        display: block;
        width: auto;
        height: 12px;
      }
    }

    .product-card-farm-name {
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:hover .product-card-farm-name {
      text-decoration: underline;
    }
  }

  .product-card-availability {
    height: 51px;
    padding: 7px 0;
    border-top: 1px solid ${({ theme }) => theme.cardDivider};
    border-bottom: 1px solid ${({ theme }) => theme.cardDivider};
    overflow: hidden;

    .product-card-availability-row {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 10px;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      letter-spacing: -0.24px;
      color: ${({ theme }) => theme.cardMutedText};
    }

    .product-card-availability-reserve {
      margin-top: 3px;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: -0.24px;
      color: ${({ theme }) => theme.cardMutedText};
    }
  }

  .product-card-cart {
    margin-top: auto;

    .amount-selector {
      width: 100%;
    }
  }

  .product-card-add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 8px 24px;
    border: none;
    border-radius: 40px;
    background-color: ${({ theme }) => theme.navDark};
    color: ${({ theme }) => theme.white};
    font-size: 14px;
    font-weight: 500;
    font-family: inherit;
    white-space: nowrap;
    cursor: pointer;
    transition: opacity 0.2s ease-in-out;

    &:hover {
      opacity: 0.9;
    }

    &.unavailable,
    &.unavailable:hover {
      background-color: ${({ theme }) => theme.navDark}99;
      opacity: 1;
      cursor: default;
    }

    > div {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
    }

    img {
      width: 16px;
      height: 16px;
    }
  }

  @media only screen and (max-width: ${breakpoints.lg}px) {
    .product-card-tags {
      top: 0.75rem;
      left: 0.75rem;
      right: 0.75rem;
      gap: 0.25rem;
    }

    .product-card-tag {
      height: 1.375rem;
      padding: 0.1875rem 0.25rem;
    }

    .product-card-qmarks {
      gap: 0.25rem;
      height: 1.875rem;

      .product-card-qmark {
        height: 1.875rem;
        padding: 0.1875rem 0.4375rem;
      }

      .product-card-qmark img {
        height: 1.5rem;
      }
    }

    .product-card-name {
      left: 0.75rem;
      right: 0.75rem;
      bottom: 0.75rem;
    }

    .product-card-price {
      gap: 0.625rem;

      .price-current {
        .price-unit {
          margin-left: 0.25rem;
        }
      }

      .price-details {
        gap: 0.125rem;
      }
    }

    .product-card-farm {
      gap: 0.25rem;
      line-height: 0.875rem;

      .product-card-farm-pin {
        width: 0.875rem;
        height: 0.875rem;

        img {
          height: 0.75rem;
        }
      }
    }

    .product-card-availability {
      padding: 0.4375rem 0;

      .product-card-availability-row {
        gap: 0.625rem;
        font-size: 0.75rem;
        line-height: 1rem;
      }

      .product-card-availability-reserve {
        margin-top: 0.1875rem;
        font-size: 0.75rem;
        line-height: 1rem;
      }
    }

    .product-card-add-btn {
      gap: 0.625rem;
      padding: 0.5rem 1.5rem;

      > div {
        width: 1rem;
        height: 1rem;
      }

      img {
        width: 1rem;
        height: 1rem;
      }
    }

    .product-card-body {
      gap: 0.5rem;
      padding: 0.75rem;
    }

    .product-card-name {
      font-size: 1rem;
      line-height: 1.44;
    }

    .product-card-tag {
      font-size: 0.75rem;
      line-height: 1rem;
    }

    .product-card-price {
      .price-current {
        font-size: 1.6875rem;

        .price-currency {
          font-size: 0.9375rem;
        }

        .price-unit {
          font-size: 0.75rem;
        }
      }

      .price-details {
        font-size: 0.625rem;
        line-height: 0.875rem;
      }
    }

    .product-card-farm {
      font-size: 0.75rem;
    }

    .product-card-availability {
      height: 3.1875rem;

      .product-card-availability-row,
      .product-card-availability-reserve {
        font-size: 0.75rem;
        line-height: 1rem;
      }
    }

    .product-card-add-btn {
      font-size: 0.875rem;
    }
  }

  @media only screen and (max-width: ${breakpoints.xs}px) {
    .product-card-cover,
    &.farm-view .product-card-cover {
      aspect-ratio: 382 / 173;
    }
  }
`;

const farmCardRow = css`
  flex-direction: row;
  padding: 4px;

  .farm-card-cover {
    width: clamp(120px, calc(100% - 198px), 152px);
    border-radius: 0;
  }

  .farm-card-logo {
    inset: 0;
  }

  .farm-card-top {
    top: 4px;
    left: 4px;
    max-width: calc(100% - 8px);
    padding: 2px 4px 4px;
    font-weight: 600;
    line-height: 16px;
  }

  .farm-card-cooperative {
    left: 80px;
  }

  .farm-card-footer {
    flex-wrap: wrap;
    gap: 4px;
  }

  .farm-card-qmarks {
    min-height: 22px;
    margin-left: auto;
  }

  .farm-card-qmark {
    min-width: 30px;
    height: 22px;
    padding: 0 4px;

    img {
      height: 17px;
    }
  }
`;

const farmCardRowMobile = css`
  flex-direction: row;
  padding: 0.25rem;

  .farm-card-cover {
    width: clamp(7.5rem, calc(100% - 12.375rem), 9.5rem);
    border-radius: 0;
  }

  .farm-card-logo {
    inset: 0;
  }

  .farm-card-top {
    top: 0.25rem;
    left: 0.25rem;
    max-width: calc(100% - 0.5rem);
    padding: 0.125rem 0.25rem 0.25rem;
    font-weight: 600;
    line-height: 1rem;
  }

  .farm-card-cooperative {
    left: 5rem;
  }

  .farm-card-footer {
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .farm-card-qmarks {
    min-height: 1.375rem;
    margin-left: auto;
  }

  .farm-card-qmark {
    min-width: 1.875rem;
    height: 1.375rem;
    padding: 0 0.25rem;

    img {
      height: 1.0625rem;
    }
  }
`;

export const StyledFarmCard = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: 100%;
  border: 1px solid ${({ theme }) => theme.cardDivider};
  border-radius: 5px;
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.black};
  font-family: ${({ theme }) => theme.fontText};
  transition: box-shadow 0.2s ease-in-out;

  &.hoverable:hover {
    color: ${({ theme }) => theme.black};
    box-shadow: 0 4px 16px ${({ theme }) => theme.softShadowGray};
  }

  .farm-card-cover {
    position: relative;
    flex: none;
    aspect-ratio: 1 / 1;
    border-radius: 4px 4px 0 0;
    background-color: ${({ theme }) => theme.white};
  }

  .farm-card-logo {
    position: absolute;
    inset: 12px;
    display: flex;
  }

  .farm-card-logo .ant-image {
    display: flex;
    width: 100%;
    height: 100%;
  }

  .farm-card-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    mix-blend-mode: darken;
  }

  .farm-card-top {
    position: absolute;
    top: 10px;
    left: 10px;
    padding: 2px 6px;
    border-radius: 2px;
    background-color: ${({ theme }) => theme.topPriceBgColor};
    color: ${({ theme }) => theme.white};
    font-size: 12px;
    line-height: 1.4;
  }

  .farm-card-cooperative {
    position: absolute;
    top: -11px;
    left: 50%;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    transform: translateX(-50%);
    border: 1px solid ${({ theme }) => theme.brand02};
    border-radius: 50%;
    background-color: ${({ theme }) => theme.white};
    overflow: hidden;

    img {
      width: 70%;
      height: 70%;
      object-fit: contain;
    }
  }

  .farm-card-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    padding: 12px;
  }

  .farm-card-name,
  .farm-card-address,
  .farm-card-categories {
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .farm-card-name {
    font-size: 16px;
    font-weight: 600;
    line-height: 23px;
    color: ${({ theme }) => theme.black};
  }

  .farm-card-address {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.cardMutedText};

    span:last-child {
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: inherit;
    }
  }

  .farm-card-pin {
    display: inline-flex;
    flex: none;
    width: 8.44px;
    height: 12px;

    img {
      display: block;
    }
  }

  .farm-card-categories {
    font-family: ${({ theme }) => theme.profileFontFamilySecondary},
      ${({ theme }) => theme.fontText};
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    color: ${({ theme }) => theme.cardTagText};
  }

  .farm-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: auto;
    padding-top: 8px;
    border-top: 1px solid ${({ theme }) => theme.cardDivider};
  }

  .farm-card-count {
    flex: none;
    font-family: ${({ theme }) => theme.profileFontFamilySecondary},
      ${({ theme }) => theme.fontText};
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    color: ${({ theme }) => theme.cardAccentText};
  }

  .farm-card-qmarks {
    display: flex;
    align-items: center;
    gap: 2px;
    min-width: 0;
    min-height: 30px;
  }

  .farm-card-qmark {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: 30px;
    padding: 2px 6px;
    border: 0.5px solid ${({ theme }) => theme.lightGrey};
    border-radius: 2px;
    background-color: ${({ theme }) => theme.white};
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.cardMutedText};

    .ant-image {
      display: flex;
    }

    img {
      height: 24px;
      width: auto;
      aspect-ratio: ${QUALITY_MARK_ASPECT_RATIO};
      object-fit: contain;
    }
  }

  .farm-card-qmark-mobile {
    display: none;
  }

  &.row {
    ${farmCardRow}
  }

  @media only screen and (max-width: ${breakpoints.lg}px) {
    .farm-card-logo {
      inset: 0.75rem;
    }

    .farm-card-top {
      top: 0.625rem;
      left: 0.625rem;
      padding: 0.125rem 0.375rem;
      font-size: 0.75rem;
    }

    .farm-card-cooperative {
      top: -0.6875rem;
      width: 2.375rem;
      height: 2.375rem;
    }

    .farm-card-body {
      gap: 0.5rem;
      padding: 0.75rem;
    }

    .farm-card-name {
      font-size: 1rem;
      line-height: 1.4375rem;
    }

    .farm-card-address {
      gap: 0.25rem;
      font-size: 0.75rem;
    }

    .farm-card-pin {
      width: 0.5275rem;
      height: 0.75rem;
    }

    .farm-card-categories {
      font-size: 0.875rem;
    }

    .farm-card-footer {
      gap: 0.5rem;
      padding-top: 0.5rem;
    }

    .farm-card-count {
      font-size: 0.875rem;
    }

    .farm-card-qmarks {
      gap: 0.125rem;
      min-height: 1.875rem;
    }

    .farm-card-qmark {
      height: 1.875rem;
      padding: 0.125rem 0.375rem;
      font-size: 0.75rem;

      img {
        height: 1.5rem;
      }
    }

    &.row {
      ${farmCardRowMobile}
    }
  }

  @media only screen and (max-width: ${breakpoints.xs}px) {
    &.mobile-row {
      ${farmCardRowMobile}
      border: none;
    }

    &.row,
    &.mobile-row {
      .farm-card-qmark-desktop {
        display: none;
      }

      .farm-card-qmark-mobile {
        display: flex;
      }
    }
  }
`;

export const StyledCategoryCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 194px;
  max-width: 467px;
  width: 100%;
  border-radius: 5px;
  font-family: ${(props) => props.theme.fontText};
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 194px;
    object-fit: cover;
  }

  .ant-image {
    display: unset !important;
    width: 100%;
  }

  .ant-card-body {
    padding: 8px 20px;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 6px;

    p {
      margin: 0;
    }
  }

  .category-title {
    font-size: 18px;
    font-weight: 500;
    line-height: 38px;
  }

  .product-count {
    color: ${(props) => props.theme.grey};
    font-family: Albert Sans;
    font-size: 16px;
    font-weight: 400;
    line-height: 25px;
  }

  @media only screen and (max-width: ${breakpoints.xs}px) {
    min-height: 194px;
  }
`;

export const StyledCartProductCard = styled(Card)`
  font-family: ${(props) => props.theme.fontText};
  display: flex;
  flex-direction: row;
  padding: 25px 0 25px 0;
  border: none;
  border-radius: 0;

  .ant-image {
    display: unset !important;
  }

  .ant-card-cover {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    min-height: 115px;
    max-height: 115px;
    min-width: 115px;
    max-width: 115px;
    border-radius: 5px !important;
    overflow: hidden;

    img {
      border-radius: 5px;
      height: auto;

      @media only screen and (max-width: ${breakpoints.xs}px) {
        height: 136px;
      }
    }
  }

  .ant-card-body {
    display: flex;
    flex-direction: row;
    padding: 5px 20px 5px 20px;
    width: 100%;

    .left-side-container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: 100%;
      font-family: ${(props) => props.theme.counterBrown};

      .cart-product-title {
        font-weight: 600;
        font-size: 16px;
        margin: 0;
      }

      .quality-marks-container {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 5px;
        margin-top: 5px;
        margin-bottom: 5px;

        .quality-mark {
          width: 36px;
          aspect-ratio: ${QUALITY_MARK_ASPECT_RATIO};
          overflow: hidden;

          .ant-image {
            display: flex;
            width: 100%;
            height: 100%;

            img {
              height: 100%;
              object-fit: contain;
              vertical-align: inherit;
            }
          }
        }
      }

      .amount-selector-wrapper {
        max-width: fit-content;
      }
    }

    .right-side-container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-end;

      .remove-cart-product-btn {
        background-color: transparent;
        border: 0;
        font-size: 16px;
        font-weight: 400;
        color: ${(props) => props.theme.removeProductRed};
        cursor: pointer;
        user-select: none;
      }

      .price-container {
        display: flex;
        flex-direction: column-reverse;
        justify-content: flex-start;
        align-items: flex-end;
        min-width: 104px;

        .price-type-wrapper {
          display: flex;
          flex-direction: column;
          align-items: flex-end;

          .price-grey {
            font-weight: 700;
            font-size: 16px;
            font-family: ${(props) => props.theme.fontText};
            color: ${(props) => props.theme.black};
          }
        }

        .price-type-second,
        .package-price {
          position: relative;
          display: flex;
          flex-direction: row;
          font-size: 13px;
          color: ${(props) => props.theme.grey};
          gap: 4px;
        }

        .price-type-second::before {
          position: absolute;
          height: 1px;
          width: 110%;
          left: -5px;
          bottom: 10px;
          transform: rotate(-5deg);
          background-color: ${(props) => props.theme.discountCrossRed};
          content: '';
        }

        .grey-price {
          display: flex;
          flex-direction: row;
          line-height: 18px;
          color: ${(props) => props.theme.red};
          gap: 4px;
        }

        .price-type-container {
          display: flex;
          flex-direction: row;
          gap: 4px;

          .price {
            font-weight: 700;
            font-size: 16px;
            font-family: ${(props) => props.theme.fontText};
            white-space: nowrap;
          }
          .type-of-price {
            font-weight: 700;
            font-size: 16px;
            font-family: ${(props) => props.theme.fontText};
          }
        }

        .grey-price,
        .package-price {
          display: flex;
          flex-direction: row;
          font-size: 13px;
          color: ${(props) => props.theme.grey};
          gap: 4px;
        }
      }
    }
  }
`;

export const StyledCartCard = styled.div`
  .price {
    white-space: nowrap;
  }

  .ant-image {
    display: unset !important;
  }

  .cart-products-content {
    display: grid;
    gap: 8px;
    grid-template-columns:
      minmax(115px, 3.6fr) 7.9fr 4.2fr minmax(32px, 4.3fr)
      minmax(72px, 2fr);
    border-radius: 5px;
    align-items: center;
    justify-items: center;

    .cart-cell-1,
    .cart-cell-2 {
      justify-self: start;
    }

    .cart-cell-1 {
      display: flex;
      flex-direction: row-reverse;

      .cart-products-remove-button {
        height: 18px;
        width: 18px;
        position: absolute;
        z-index: 5;
        transform: translate(50%, -50%);

        img {
          height: 18px;
          width: 18px;
        }
      }

      .ant-image-mask {
        border-radius: 5px;
      }

      img {
        border-radius: 5px;
        width: 115px;
        height: 115px;
      }

      @media only screen and (max-width: ${breakpoints.md}px) {
        padding: 0px;

        img {
          width: 98px;
          height: 98px;
        }
      }
    }

    .cart-cell-2 {
      align-self: baseline;

      .cart-product-farm-name {
        cursor: pointer;
        color: ${(props) => props.theme.grey};
        margin-top: 0;
        height: 20px;
        overflow: hidden;
        text-overflow: ellipsis;

        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        white-space: normal;
      }

      .cart-product-name-row {
        display: flex;
        align-items: center;
        gap: 8px;

        .cart-product-name {
          cursor: pointer;
          font-weight: 600;
          margin: 0;
        }

        .cart-product-quality-marks {
          display: flex;
          align-items: center;
          gap: 5px;

          .quality-mark {
            height: 21px;
            width: auto;
            aspect-ratio: ${QUALITY_MARK_ASPECT_RATIO};
            overflow: hidden;

            .ant-image {
              display: flex;
              width: 100%;
              height: 100%;

              img {
                height: 100%;
                object-fit: contain;
                vertical-align: inherit;
              }
            }
          }
        }
      }

      .cart-product-stock-info {
        color: ${(props) => props.theme.productCount};
        font-size: 14px;
        margin-top: 4px;

        @media only screen and (max-width: ${breakpoints.md}px) {
          display: none;
        }
      }

      .quality-marks-container {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin-top: 4px;

        .quality-mark {
          width: 40px;
          aspect-ratio: ${QUALITY_MARK_ASPECT_RATIO};
          overflow: hidden;

          .ant-image {
            display: flex;
            width: 100%;
            height: 100%;

            img {
              height: 100%;
              object-fit: contain;
              vertical-align: inherit;
            }
          }
        }
      }

      @media only screen and (max-width: ${breakpoints.md}px) {
        padding-left: 10px;

        .cart-product-farm-name {
          display: none;
        }

        .cart-product-name-row .cart-product-name {
          margin-top: 0;
          font-weight: 500;
        }
      }
    }

    .cart-cell-3,
    .cart-cell-5 {
      .price-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        .price-type-wrapper {
          display: flex;
          flex-direction: column;
          align-items: flex-start;

          .price-type-second {
            position: relative;
            display: flex;
            flex-direction: row;
            align-items: center;
            font-size: 13px;
            color: ${(props) => props.theme.grey};
            font-weight: 500;
            margin-bottom: 2px;

            &::before {
              content: '';
              position: absolute;
              height: 1px;
              width: 110%;
              left: -5px;
              bottom: 8px;
              transform: rotate(-5deg);
              background-color: ${(props) => props.theme.discountCrossRed};
            }
          }

          .price-grey {
            font-size: 16px;
            font-weight: 700;
            font-family: ${(props) => props.theme.fontText};
            color: ${(props) => props.theme.black};
            line-height: 18px;
            display: flex;
            gap: 4px;
            span {
              font-weight: 400;
            }
            .type-of-price {
              font-weight: 400;
            }
          }
        }

        .package-price {
          display: flex;
          flex-direction: row;
          font-size: 13px;
          color: ${(props) => props.theme.grey};
          gap: 4px;
        }

        .price-type-container {
          display: flex;
          flex-direction: row;
          gap: 4px;

          @media only screen and (max-width: ${breakpoints.md}px) {
            margin-left: 8px;
          }

          .price {
            font-weight: 700;
            font-size: 16px;
            font-family: ${(props) => props.theme.fontText};
            line-height: 18px;
            white-space: nowrap;
            span {
              font-weight: 400;
            }
          }
          .type-of-price {
            font-weight: 400;
            font-size: 16px;
            font-family: ${(props) => props.theme.fontText};
            line-height: 18px;
            white-space: nowrap;
          }
        }
      }
    }

    .cart-cell-4 {
      .cart-product-amount-view {
        font-size: 16px;
        font-weight: 400;
        color: ${(props) => props.theme.bgColorHover};
      }
    }

    @media only screen and (max-width: ${breakpoints.md}px) {
      grid-template-columns: 98px 1fr;
      grid-template-rows: auto auto;
      align-items: start;

      .cart-cell-1 {
        grid-row: 1 / 3;
      }

      .cart-cell-2 {
        grid-column: 2;
        grid-row: 1;
      }

      .cart-cell-3 {
        grid-column: 2;
        grid-row: 2;
        justify-self: start;
        align-self: end;
      }

      .cart-cell-4,
      .cart-cell-5 {
        display: none;
      }
    }
  }

  .mobile-cart-cells {
    display: none;

    @media only screen and (max-width: ${breakpoints.md}px) {
      display: grid;
      grid-template-columns: repeat(2, 50%);
      padding: 7px;
      padding-top: 3px;

      .cart-cell-4 div[class*='amount-selector'] {
        height: 48px;
        padding: 0;
        border-radius: 5px;
        border: 1px solid ${(props) => props.theme.grey};
        border-color: transparent;
        background-color: ${(props) => props.theme.almostWhite};
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
        font-weight: 500;
        font-size: 18px;
        button {
          font-weight: 600;
        }
      }

      .cart-cell-5 {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: end;
        align-items: center;
        padding: 0 15px;
        background-color: ${(props) => props.theme.almostWhite};
        border-radius: 5px;
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;

        .price-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;

          .price-type-wrapper {
            .price-type-second {
              display: none;
            }

            .price-grey {
              font-size: 1rem;
              font-weight: 700;
              font-family: ${(props) => props.theme.fontText};
              color: ${(props) => props.theme.black};
              line-height: 18px;
              display: flex;
              gap: 4px;
              span {
                font-weight: 400;
              }
              .type-of-price {
                font-weight: 400;
              }
            }
          }

          .price-type-container {
            display: flex;
            flex-direction: row;
            gap: 4px;

            .price {
              font-weight: 700;
              font-size: 16px;
              font-family: ${(props) => props.theme.fontText};
              line-height: 18px;
              white-space: nowrap;
              span {
                font-weight: 400;
              }
            }
            .type-of-price {
              font-weight: 400;
              font-size: 16px;
              font-family: ${(props) => props.theme.fontText};
              line-height: 18px;
              white-space: nowrap;
            }
          }
        }
      }

      .cart-cell-5::after {
        position: absolute;
        top: 9px;
        left: 0;
        z-index: 0;
        width: 1px;
        height: 31px;
        background-color: ${(props) => props.theme.silverGray};
        content: '';
      }
    }
  }

  .mobile-stock-info {
    display: none;

    @media only screen and (max-width: ${breakpoints.md}px) {
      display: block;
      color: ${(props) => props.theme.productCount};
      font-size: 14px;
      padding: 4px 10px 10px;
    }
  }

  div[class*='cart-delivery-button'] {
    display: none;
    padding: 0 10px 10px 10px;
  }

  @media only screen and (max-width: ${breakpoints.md}px) {
    background-color: ${(props) => props.theme.cloudGray};
    .cart-products-content {
      padding: 10px;
    }

    div[class*='cart-delivery-button'] {
      display: block;
    }

    div[class*='cart-delivery-button'] button {
      padding: 7px 0;
      width: 100%;
      height: auto;
    }

    .cart-footer {
      display: grid;
      grid-template-columns: 1.8fr 1.2fr;
      padding: 10px;
      gap: 5px;

      .cart-footer-text {
        margin: 0;
        font-size: 1rem;
        font-weight: 300;
        line-height: 19.2px;
      }

      .cart-footer-link {
        text-align: end;
        color: ${(props) => props.theme.darkRed};
      }

      .cart-footer-link span {
        border-bottom: 2px solid ${(props) => props.theme.darkRed};
      }
    }
  }

  @media only screen and (max-width: ${breakpoints.lg}px) {
    .price-container {
      .price {
        font-size: 1rem !important;
      }

      .type-of-price {
        font-size: 1rem !important;
      }
    }
  }
`;

export const StyledListingProductCard = styled(Card)`
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 5px;
  color: ${(props) => props.theme.black};

  .ant-card-body {
    padding: 20px;
  }

  .product-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    .product-details {
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }

  .product-number {
    width: 32px;
    height: 32px;
    background-color: ${(props) => props.theme.colorPrimary};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    flex-shrink: 0;
    color: ${(props) => props.theme.white};
  }

  .product-actions {
    display: flex;
    flex-shrink: 0;

    .ant-btn:hover {
      background: none;
      color: ${(props) => props.theme.black};
    }
  }

  .action-button {
    padding: 4px 8px;
    height: auto;
    border: none;
    background: none;
    text-decoration: underline;
    cursor: pointer;
  }
`;

export const StyledDetailedDeliveryCard = styled(Card)`
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 5px;
  color: ${(props) => props.theme.black};

  .ant-card-body {
    padding: 20px;
  }

  .delivery-info-container {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .unused-products {
    color: ${(props) => props.theme.colorPrimary};
  }

  .delivery-details {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    flex: 1;

    .delivery-content {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  }

  .delivery-number {
    width: 32px;
    height: 32px;
    background-color: ${(props) => props.theme.colorPrimary};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: ${(props) => props.theme.white};
  }

  .delivery-type {
    margin-top: 20px;
  }

  .delivery-products {
    color: ${(props) => props.theme.colorPrimary};
    margin-bottom: 2px;
  }

  .delivery-actions {
    display: flex;
    flex-shrink: 0;
    gap: 8px;

    .ant-btn:hover {
      background: none;
      color: ${(props) => props.theme.black};
    }
  }

  .action-button {
    padding: 4px 8px;
    height: auto;
    border: none;
    background: none;
    text-decoration: underline;
    cursor: pointer;
  }
`;

export const StyledMobileListCard = styled(Card)`
  border: 1px solid ${(props) => props.theme.cardBorder};
  border-radius: 5px;
  padding: 10px 10px 10px 10px;
  font-size: 1rem;
  font-weight: 400;
  font-family: ${(props) => props.theme.fontFamily};

  &.is-clickable {
    cursor: pointer;
  }

  &.separated {
    .ant-card-cover {
      padding-bottom: 0;
      border-bottom: none;
      margin-bottom: 0;
    }
  }

  .ant-card-cover {
    padding-bottom: 10px;
    border-bottom: 1px solid ${(props) => props.theme.inputAddonBg};
    margin-bottom: 10px;

    .mobile-list-card-cover {
      color: ${(props) => props.theme.cardSecondElement};

      .mobile-list-card-cover-item {
        display: flex;
        justify-content: space-between;
      }
    }
  }

  .ant-card-body {
    padding: 0;

    .mobile-card-content-wrapper {
      display: flex;
      row-gap: 10px;
      flex-direction: column;

      .mobile-list-card-body-wrapper {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        row-gap: 0px;

        .mobile-list-card-body-item {
          display: flex;
          flex-direction: column;
          width: 50%;

          span:nth-child(1) {
            color: ${(props) => props.theme.cardFirstElement};
          }
          span:nth-child(2) {
            color: ${(props) => props.theme.cardSecondElement};
          }

          .transaction-total {
            font-weight: 600;
          }

          .ant-btn {
            min-height: 32px;
            min-width: 150px;
          }

          .ant-form-item {
            margin-bottom: 0;
          }
        }

        .mobile-list-card-body-item:nth-child(even) {
          text-align: end;
        }
      }

      .mobile-list-card-body-triple-section-wrapper {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        row-gap: 10px;
        padding-top: 10px;
        border-top: 1px solid ${(props) => props.theme.inputAddonBg};

        .mobile-list-card-body-triple-section-item {
          display: flex;
          flex-direction: column;
          width: 50%;

          span:nth-child(1) {
            color: ${(props) => props.theme.cardFirstElement};
          }
          span:nth-child(2) {
            color: ${(props) => props.theme.cardSecondElement};
          }

          .transaction-total {
            font-weight: 600;
          }

          .ant-btn {
            min-height: 32px;
            min-width: 150px;
          }

          .ant-form-item {
            margin-bottom: 0;
          }
        }

        .mobile-list-card-body-triple-section-item:nth-child(even) {
          text-align: end;
        }
      }
    }
  }

  .mobile-list-card-footer {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .mobile-list-card-footer-item {
      .ant-btn {
        display: flex;
        justify-content: space-between;
        width: 100%;
        height: 40px;
        border-radius: 3px;
        border-color: ${(props) => props.theme.black};
      }
    }
  }
`;
